import * as THREE from 'three';

import { HEX_SIZE } from '../map/HexMetrics';
import { STEP_SECONDS } from '../map/Movement';

/**
 * Camera controller for the isometric view.
 *
 * The camera orbits a focus point on the ground: left drag moves the focus,
 * right drag (or Alt+left, for trackpads) spins the camera around it, wheel
 * zooms. WASD moves the focus and QE spins it, mirroring the mouse. Pitch is
 * fixed, so the view can turn a full 360° horizontally but never tilts.
 */

/** Fixed elevation angle — 35.264°, the classic isometric tilt. */
export const PITCH = Math.atan(1 / Math.SQRT2);

/** Orbit radius. Irrelevant to apparent size under an orthographic camera, but it keeps the scene inside the near/far planes. */
export const DISTANCE = 20 * Math.sqrt(3);

/**
 * Starting angle, and the origin of the snap grid. Snapping relative to it
 * rather than to absolute zero keeps the original view reachable — a hex has
 * six-fold symmetry, so all six snap positions look identical anyway.
 */
const INITIAL_YAW = Math.PI / 4;
const SNAP_STEP = Math.PI / 3;

/** Radians of yaw per pixel dragged. */
const ROTATE_SPEED = 0.005;

/** Radians of yaw per second while Q or E is held — one snap step takes ~0.4 s. */
const KEY_ROTATE_SPEED = Math.PI / 1.2;

/** World units of focus travel per second at zoom 1 while a WASD key is held. */
const KEY_PAN_SPEED = 15;

/**
 * How long the camera takes to scroll to a point, by how far it has to go.
 *
 * `base + factor · √distance`, clamped. The square root is the whole idea: a
 * step away should arrive almost at once, and the far side of the map should
 * take noticeably longer without taking proportionally longer — travel time
 * that scaled with distance would make a long scroll a journey, and one that
 * ignored distance would make it a blur.
 *
 * One cell is 0,87 units, so following a walking player runs at the short end:
 * a quarter of a second per step, which trails just enough to read as a camera
 * keeping up rather than as one glued on.
 */
const GLIDE_BASE = 0.12;
const GLIDE_FACTOR = 0.13;
const GLIDE_MIN = 0.2;
const GLIDE_MAX = 1.4;

/**
 * How the camera keeps up with something that is walking.
 *
 * A cell per server cooldown — the same rate the pawn moves at — so a followed
 * player sits still in the frame instead of drifting back and forth. Anything
 * further behind than that is closed inside `FOLLOW_CATCHUP`, which is what
 * turns a burst of steps into a slightly quicker glide rather than a permanent
 * lag.
 *
 * Constant speed, and deliberately not the eased `glideTo`: easing per step
 * means accelerating and braking several times a second, which is exactly the
 * pulsing that easing is normally there to avoid.
 *
 * Taken from the movement rules rather than written out: this used to be the
 * cooldown spelled as 0,25 and would have stayed at the old pace when the game
 * was slowed down, leaving the camera drifting ahead of the player it follows.
 */
const FOLLOW_SPEED = (HEX_SIZE * Math.sqrt(3)) / STEP_SECONDS;
const FOLLOW_CATCHUP = 0.3;

/**
 * The opening move: a slow turn and a push in, on arriving in the world.
 *
 * Thirty degrees of rotation and a little under twice the distance, over two
 * seconds and a bit. Deliberately small — the point is to arrive somewhere
 * rather than to be shown a film, and anything longer is something the player
 * sits through on every reload.
 */
const INTRO_SECONDS = 2.2;
const INTRO_TURN = Math.PI / 6;
const INTRO_PULL_BACK = 0.55;

function glideSeconds(distance: number): number {
  return THREE.MathUtils.clamp(
    GLIDE_BASE + GLIDE_FACTOR * Math.sqrt(distance),
    GLIDE_MIN,
    GLIDE_MAX
  );
}

/**
 * Keyboard movement is read from `code`, not `key`, so the keys stay under the
 * same fingers on layouts where WASD sits elsewhere (AZERTY, Dvorak).
 */
const PAN_KEYS = ['KeyW', 'KeyS', 'KeyA', 'KeyD'];
const ROTATE_KEYS = ['KeyQ', 'KeyE'];

export class CameraController {
  private camera: THREE.OrthographicCamera;
  private domElement: HTMLElement;

  // Drag state
  private isPanning = false;
  private isRotating = false;
  private dragStart = new THREE.Vector2();
  private dragDelta = new THREE.Vector2();

  /**
   * Keys currently held. Movement is applied once per frame from this set
   * rather than on the event, so speed comes from elapsed time instead of the
   * operating system's key-repeat rate.
   */
  private pressed = new Set<string>();

  // Zoom state
  /**
   * How far in and out the camera goes.
   *
   * Unchanged by the resolution going up, and that is worth knowing rather than
   * assuming: a zoom means buffer pixels per world unit, and raising the buffer
   * by half while raising the pixels per unit by half leaves the visible world
   * exactly where it was. Views players have saved keep meaning what they meant.
   */
  private minZoom = 1.0;
  private maxZoom = 6.5;
  private zoomSpeed = 0.1;

  // Pan speed (adjusted for orthographic camera)
  private panSpeed = 0.05;

  // Orbit state. Position is derived from these every frame.
  /**
   * Where the camera is pointed, in world units — **including height**.
   *
   * The height matters because the projection is oblique: a point one unit up
   * lands almost a unit up the screen, so a focus pinned to sea level while the
   * player stands twenty levels up puts them well above the middle of the
   * frame. Centring and following therefore say which height they mean; drag,
   * WASD and zoom leave it where it was, because none of them is a statement
   * about how high the ground is.
   */
  private focus = new THREE.Vector3();
  private targetFocus = new THREE.Vector3();
  private yaw = INITIAL_YAW;
  /**
   * Czy puszczony obrót staje na najbliższej krawędzi heksa (ustawienie
   * „Przyciąganie obrotu"). Przełączenie niczego nie obraca od razu: kąt
   * wyrówna się przy następnym puszczeniu.
   */
  snapping = true;
  private targetYaw = INITIAL_YAW;
  private targetZoom = 1.0;

  // Smoothing factor (0-1, higher = faster)
  private smoothing = 0.15;

  /**
   * Ile z pozostałej drogi zamknąć w tej klatce — **liczone z czasu, nie
   * z klatki**.
   *
   * `smoothing` jest ułamkiem na klatkę przy sześćdziesięciu klatkach na
   * sekundę; wzięty wprost znaczy co innego przy każdej długości klatki. Cel
   * obrotu rośnie **z czasem** (`KEY_ROTATE_SPEED * frameTime`), więc dłuższa
   * klatka odsuwała go dalej, a dociąganie zamykało tyle samo procent co zawsze
   * — obrót przystawał i zaraz nadganiał. Pod Q i E czytało się to jak
   * przyciąganie do kątów heksa, choć nic tam nie przyciągało.
   */
  private caught(deltaTime: number): number {
    return 1 - Math.pow(1 - this.smoothing, Math.max(0, deltaTime) * 60);
  }

  /**
   * The arrival: yaw and zoom sliding from where the world opened to where the
   * player left off. Separate from `glide`, which moves the focus — this one
   * turns and pushes in while the focus already sits on the player.
   */
  private intro: {
    fromYaw: number;
    toYaw: number;
    fromZoom: number;
    toZoom: number;
    elapsed: number;
  } | null = null;

  /**
   * A scroll in progress, run on a clock rather than on the exponential
   * smoothing the rest of the camera uses.
   *
   * Smoothing converges in about the same time whatever the distance, which is
   * exactly what a scroll should not do: it makes a long one start at a sprint.
   * A timed tween is the only way for travel time to depend on how far there is
   * to travel.
   */
  private glide: {
    from: THREE.Vector3;
    to: THREE.Vector3;
    elapsed: number;
    duration: number;
  } | null = null;

  /**
   * Called when the person at the keyboard takes the camera back — a drag or a
   * WASD key. Rotation and zoom are not that: they do not move the focus, so
   * whatever the camera was following it can keep following while being spun
   * around it.
   */
  onUserPan: (() => void) | null = null;

  /**
   * What the camera is keeping up with, while it is keeping up with anything.
   *
   * Held apart from `glide`: a scroll is a journey with a beginning and an end,
   * and this is a standing instruction that outlives any number of them.
   */
  private follow: THREE.Vector3 | null = null;

  // Scratch vectors for the pan basis, reused to avoid per-event allocation
  private screenRight = new THREE.Vector3();
  private screenUp = new THREE.Vector3();

  constructor(camera: THREE.OrthographicCamera, domElement: HTMLElement) {
    this.camera = camera;
    this.domElement = domElement;

    this.targetZoom = camera.zoom;

    this.setupEventListeners();
    this.applyToCamera();
  }

  /**
   * Setup mouse event listeners
   */
  private setupEventListeners(): void {
    this.domElement.addEventListener('mousedown', this.onMouseDown);
    this.domElement.addEventListener('mousemove', this.onMouseMove);
    this.domElement.addEventListener('mouseup', this.onMouseUp);
    this.domElement.addEventListener('mouseleave', this.onMouseUp);

    // Right drag rotates, so the browser menu has to stay out of the way
    this.domElement.addEventListener('contextmenu', this.onContextMenu);

    this.domElement.addEventListener('wheel', this.onWheel, { passive: false });

    // Touch support for mobile — single finger pans, rotation is mouse-only
    this.domElement.addEventListener('touchstart', this.onTouchStart, { passive: false });
    this.domElement.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.domElement.addEventListener('touchend', this.onTouchEnd, { passive: false });

    // Keyboard listens on the window: the canvas is not focusable, so it never
    // sees a key event of its own. Losing focus mid-press swallows the keyup,
    // which would leave the camera drifting forever — blur clears the set.
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
  }

  /**
   * Mouse down handler
   */
  private onMouseDown = (event: MouseEvent): void => {
    // Alt+left is the trackpad route to orbiting, where a right drag turns into
    // a two-finger gesture and gets eaten by scrolling. The modifier is read
    // once here: releasing Alt mid-drag must not turn an orbit into a pan.
    if (event.button === 2 || (event.button === 0 && event.altKey)) {
      this.isRotating = true;
      this.stopIntro();
    } else if (event.button === 0) {
      this.isPanning = true;
      this.takeOver();
    } else {
      return;
    }

    this.dragStart.set(event.clientX, event.clientY);
    this.domElement.style.cursor = 'grabbing';
  };

  /**
   * Mouse move handler (pan or orbit)
   */
  private onMouseMove = (event: MouseEvent): void => {
    if (!this.isPanning && !this.isRotating) return;

    this.dragDelta.set(
      event.clientX - this.dragStart.x,
      event.clientY - this.dragStart.y
    );

    if (this.isPanning) {
      this.pan(this.dragDelta.x, this.dragDelta.y);
    } else {
      // Vertical movement is ignored — that is what keeps pitch locked
      this.rotate(this.dragDelta.x);
    }

    this.dragStart.set(event.clientX, event.clientY);
  };

  /**
   * Mouse up handler
   */
  private onMouseUp = (): void => {
    if (this.isRotating) {
      this.snapYaw();
    }

    this.isPanning = false;
    this.isRotating = false;
    this.domElement.style.cursor = 'grab';
  };

  private onContextMenu = (event: MouseEvent): void => {
    event.preventDefault();
  };

  private isPaused = false;

  /**
   * Stand down while something else has the keyboard.
   *
   * Set while the game menu is open. The overlay covers the canvas, so the mouse
   * never reaches this — the keyboard does, because it listens on the window,
   * and WASD scrolling the map behind a menu is the map being moved by somebody
   * who thought they were typing. Keys held when this is set are dropped the way
   * `onBlur` drops them, or the camera would drift forever on a keyup nobody
   * sees.
   */
  set paused(value: boolean) {
    this.isPaused = value;
    if (value) this.onBlur();
  }

  get paused(): boolean {
    return this.isPaused;
  }

  /**
   * Keyboard down handler. Modified presses are left alone so browser and OS
   * shortcuts keep working.
   */
  private onKeyDown = (event: KeyboardEvent): void => {
    if (this.paused) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (!PAN_KEYS.includes(event.code) && !ROTATE_KEYS.includes(event.code)) return;

    this.pressed.add(event.code);
  };

  /**
   * Keyboard up handler. Releasing the last rotation key settles on the nearest
   * snap position, the same way ending a right drag does.
   */
  private onKeyUp = (event: KeyboardEvent): void => {
    if (!this.pressed.delete(event.code)) return;

    if (ROTATE_KEYS.includes(event.code) && !this.isRotationKeyHeld()) {
      this.snapYaw();
    }
  };

  private onBlur = (): void => {
    const wasRotating = this.isRotationKeyHeld();
    this.pressed.clear();

    if (wasRotating) {
      this.snapYaw();
    }
  };

  private isRotationKeyHeld(): boolean {
    return ROTATE_KEYS.some((code) => this.pressed.has(code));
  }

  /**
   * Mouse wheel handler (zoom)
   */
  private onWheel = (event: WheelEvent): void => {
    event.preventDefault();
    // Zooming is the other half of what the arrival animates.
    this.stopIntro();

    const delta = event.deltaY;
    const zoomDelta = delta > 0 ? -this.zoomSpeed : this.zoomSpeed;

    this.targetZoom = THREE.MathUtils.clamp(
      this.targetZoom + zoomDelta,
      this.minZoom,
      this.maxZoom
    );
  };

  /**
   * Touch start handler
   */
  private onTouchStart = (event: TouchEvent): void => {
    event.preventDefault();

    if (event.touches.length === 1) {
      this.isPanning = true;
      this.takeOver();
      this.dragStart.set(event.touches[0].clientX, event.touches[0].clientY);
    }
  };

  /**
   * Touch move handler
   */
  private onTouchMove = (event: TouchEvent): void => {
    event.preventDefault();

    if (event.touches.length === 1 && this.isPanning) {
      this.dragDelta.set(
        event.touches[0].clientX - this.dragStart.x,
        event.touches[0].clientY - this.dragStart.y
      );

      this.pan(this.dragDelta.x, this.dragDelta.y);

      this.dragStart.set(event.touches[0].clientX, event.touches[0].clientY);
    }
  };

  /**
   * Touch end handler
   */
  private onTouchEnd = (): void => {
    this.isPanning = false;
  };

  /**
   * Move the focus point by a pixel delta
   */
  private pan(deltaX: number, deltaY: number): void {
    // Orthographic zoom scales how much world a pixel covers
    const factor = this.panSpeed / this.camera.zoom;

    this.readScreenBasis();

    // Grab-and-drag: the map follows the cursor, so the focus moves against it.
    // Browser deltaY grows downwards, which cancels the second negation.
    this.targetFocus.addScaledVector(this.screenRight, -deltaX * factor);
    this.targetFocus.addScaledVector(this.screenUp, deltaY * factor);
  }

  /**
   * Load the ground-plane screen axes into the scratch vectors.
   *
   * Taken straight from the camera basis rather than assuming a fixed rotation:
   * column 0 is screen-right, column 1 is screen-up. Reading them here is what
   * makes movement follow the current yaw for free.
   */
  private readScreenBasis(): void {
    this.screenRight.setFromMatrixColumn(this.camera.matrix, 0);
    this.screenUp.setFromMatrixColumn(this.camera.matrix, 1);
    this.screenRight.y = 0;
    this.screenUp.y = 0;
    this.screenRight.normalize();
    this.screenUp.normalize();
  }

  /**
   * Move and spin from the keys held this frame.
   *
   * Unlike the mouse, this is push-the-camera rather than grab-the-map: W walks
   * the focus away from the viewer, D to the right.
   */
  private applyKeyboard(deltaTime: number): void {
    // A stall — chunk rebuild, a backgrounded tab — hands back a frame worth
    // seconds. Charging the whole gap would teleport the focus across the map,
    // so a held key buys at most one slow frame of travel.
    const frameTime = Math.min(deltaTime, 0.1);

    const spin =
      (this.pressed.has('KeyE') ? 1 : 0) - (this.pressed.has('KeyQ') ? 1 : 0);

    if (spin !== 0) {
      // Q and E do not take the camera back — they turn it without moving what
      // it is over — but turning is exactly what the arrival is doing, so it
      // has to give way.
      this.stopIntro();
      this.targetYaw += spin * KEY_ROTATE_SPEED * frameTime;
    }

    const forward =
      (this.pressed.has('KeyW') ? 1 : 0) - (this.pressed.has('KeyS') ? 1 : 0);
    const strafe =
      (this.pressed.has('KeyD') ? 1 : 0) - (this.pressed.has('KeyA') ? 1 : 0);

    if (forward === 0 && strafe === 0) return;

    // A key that moves the focus is the camera being taken back, the same as a
    // drag. Q and E are not: they spin the view without moving what it is over.
    this.takeOver();

    // Zoom divides the step for the same reason it divides the drag factor:
    // pulling back should not make the map crawl.
    let step = (KEY_PAN_SPEED * frameTime) / this.camera.zoom;

    // Two axes at once would otherwise travel √2 times faster than one
    if (forward !== 0 && strafe !== 0) step *= Math.SQRT1_2;

    this.readScreenBasis();
    this.targetFocus.addScaledVector(this.screenRight, strafe * step);
    this.targetFocus.addScaledVector(this.screenUp, forward * step);
  }

  /**
   * Orbit the camera by a horizontal pixel delta.
   *
   * targetYaw is deliberately unbounded — letting it run past ±360° means the
   * smoothed value chases it without ever crossing a wrap-around seam.
   */
  private rotate(deltaX: number): void {
    this.targetYaw += deltaX * ROTATE_SPEED;
  }

  /** Settle on the nearest snap position once the drag ends. */
  private snapYaw(): void {
    if (!this.snapping) return;
    const steps = Math.round((this.targetYaw - INITIAL_YAW) / SNAP_STEP);
    this.targetYaw = INITIAL_YAW + steps * SNAP_STEP;
  }

  /** Derive camera position and orientation from focus, yaw and the fixed pitch. */
  private applyToCamera(): void {
    const horizontal = Math.cos(PITCH) * DISTANCE;

    this.camera.position.set(
      this.focus.x + Math.sin(this.yaw) * horizontal,
      this.focus.y + Math.sin(PITCH) * DISTANCE,
      this.focus.z + Math.cos(this.yaw) * horizontal
    );
    this.camera.lookAt(this.focus);
  }

  /**
   * Update camera (smooth interpolation)
   */
  /**
   * The point on the ground the camera orbits.
   *
   * Read by the terrain streamer to decide which chunks to hold: what the
   * camera is looking at is what has to be loaded.
   */
  /**
   * Put the camera's focus on a point on the ground.
   *
   * `immediate` skips the smoothing, which is what entering the world wants:
   * the opening frame should already be over the player rather than gliding
   * towards them from wherever the camera happened to start.
   */
  focusOn(x: number, z: number, immediate = false, y = this.targetFocus.y): void {
    this.glide = null;
    this.follow = null;
    this.targetFocus.set(x, y, z);
    if (immediate) {
      this.focus.copy(this.targetFocus);
      this.applyToCamera();
    }
  }

  /**
   * Scroll to a point, taking longer the further away it is.
   *
   * Replaces any scroll already running rather than queueing behind it: the
   * newest destination is the one that is wanted, and following a walking
   * player asks for a new one every quarter of a second.
   */
  glideTo(x: number, z: number, y = this.targetFocus.y): void {
    const to = new THREE.Vector3(x, y, z);
    const distance = this.focus.distanceTo(to);

    this.targetFocus.copy(to);
    this.glide = {
      from: this.focus.clone(),
      to,
      elapsed: 0,
      duration: glideSeconds(distance),
    };
  }

  /**
   * Keep the camera on a point that keeps moving.
   *
   * Repeated as the target moves — every step, for a walking player. While a
   * scroll is still running it retargets that scroll instead, so a click from
   * across the map arrives where the player *is* rather than where they were
   * when the button went down.
   */
  followTo(x: number, z: number, y = this.targetFocus.y): void {
    const to = new THREE.Vector3(x, y, z);
    this.targetFocus.copy(to);

    if (this.glide) {
      this.glide.to.copy(to);
    }

    if (this.follow) {
      this.follow.copy(to);
    } else {
      this.follow = to;
    }
  }

  /** Stop keeping up with anything, leaving the camera where it is. */
  releaseFollow(): void {
    this.follow = null;
  }

  /** Where the camera is pointed and how close in, for storing and restoring. */
  /**
   * Where the camera is pointing **now**, as opposed to where it is heading.
   *
   * `view` reports the target, which is what gets saved: it is where the camera
   * will be once it stops. Anything drawn every frame — the compass — needs the
   * value it is actually looking from, or it lags behind the world by the
   * length of the smoothing.
   */
  get heading(): number {
    return this.yaw;
  }

  get view(): { yaw: number; zoom: number } {
    return { yaw: this.targetYaw, zoom: this.targetZoom };
  }

  /**
   * Arrive at a view: start turned away and pulled back, then settle into it.
   *
   * The destination is what the player left last time, or the opening view on a
   * first visit. Both look the same from inside — the camera lands somewhere
   * deliberate either way.
   */
  arriveAt(yaw: number, zoom: number): void {
    const toZoom = THREE.MathUtils.clamp(zoom, this.minZoom, this.maxZoom);

    this.intro = {
      fromYaw: yaw - INTRO_TURN,
      toYaw: yaw,
      fromZoom: Math.max(this.minZoom, toZoom * INTRO_PULL_BACK),
      toZoom,
      elapsed: 0,
    };

    this.yaw = this.intro.fromYaw;
    this.targetYaw = this.intro.toYaw;
    this.camera.zoom = this.intro.fromZoom;
    this.targetZoom = toZoom;
    this.camera.updateProjectionMatrix();
    this.applyToCamera();
  }

  /**
   * Put the focus at this height without moving it sideways.
   *
   * For the one case that cannot say the height up front: the opening move
   * starts **before the ground under the player has been fetched**, because the
   * terrain streams in around wherever the camera is already looking. There is
   * nothing to aim at yet, so the arrival begins at whatever height the focus
   * had and is seated the moment the ground answers.
   *
   * Everything in flight is moved with it, so a scroll or a follow that is
   * already running does not drag the view back down to where it started.
   */
  seatAt(y: number): void {
    this.focus.y = y;
    this.targetFocus.y = y;

    if (this.glide) {
      this.glide.from.y = y;
      this.glide.to.y = y;
    }
    this.follow?.setY(y);

    this.applyToCamera();
  }

  /**
   * The camera is being driven by hand again: stop wherever it is.
   *
   * Stopping *where it is* rather than where it was going is what keeps a drag
   * from snapping back — the scroll's destination stops being the target the
   * moment somebody grabs the map.
   */
  /**
   * End the arrival where it currently stands.
   *
   * Both the angle and the zoom are pinned to what they are at that instant,
   * not left pointing at the destination: cancelling the animation and then
   * gliding to the same place anyway is not cancelling anything.
   *
   * Called by every input that drives the camera directly, including the two
   * that are not "taking the camera back" — spinning and zooming leave a follow
   * running, but they are exactly what this animates, so it has to yield.
   */
  private stopIntro(): void {
    if (!this.intro) return;

    this.intro = null;
    this.targetYaw = this.yaw;
    this.targetZoom = this.camera.zoom;
  }

  private takeOver(): void {
    this.stopIntro();

    if (this.glide || this.follow) {
      this.glide = null;
      this.follow = null;
      this.targetFocus.copy(this.focus);
    }
    this.onUserPan?.();
  }

  get focusPoint(): THREE.Vector3 {
    return this.focus;
  }

  update(deltaTime: number): void {
    this.applyKeyboard(deltaTime);
    // Jedna liczba na całą klatkę: ognisko, obrót i przybliżenie mają gonić swój
    // cel tak samo, bo są jednym ruchem kamery.
    const caught = this.caught(deltaTime);

    if (this.intro) {
      this.intro.elapsed += deltaTime;
      const t = Math.min(1, this.intro.elapsed / INTRO_SECONDS);
      const eased = t * t * (3 - 2 * t);

      this.yaw = THREE.MathUtils.lerp(this.intro.fromYaw, this.intro.toYaw, eased);
      this.camera.zoom = THREE.MathUtils.lerp(this.intro.fromZoom, this.intro.toZoom, eased);
      this.camera.updateProjectionMatrix();

      if (t >= 1) this.intro = null;
      this.applyToCamera();
      return;
    }

    if (this.glide) {
      this.glide.elapsed += deltaTime;
      const t = Math.min(1, this.glide.elapsed / this.glide.duration);
      // Smoothstep: leaves and arrives at rest, so a scroll neither jolts at the
      // start nor stops dead at the end.
      const eased = t * t * (3 - 2 * t);
      this.focus.lerpVectors(this.glide.from, this.glide.to, eased);
      if (t >= 1) this.glide = null;
    } else if (this.follow) {
      // Constant speed, with enough extra to close a gap rather than trail it.
      const remaining = this.focus.distanceTo(this.follow);
      if (remaining > 1e-4) {
        const step = Math.max(FOLLOW_SPEED, remaining / FOLLOW_CATCHUP) * deltaTime;
        if (step >= remaining) {
          this.focus.copy(this.follow);
        } else {
          this.focus.lerp(this.follow, step / remaining);
        }
      }
    } else {
      this.focus.lerp(this.targetFocus, caught);
    }

    this.yaw = THREE.MathUtils.lerp(this.yaw, this.targetYaw, caught);

    this.applyToCamera();

    if (Math.abs(this.camera.zoom - this.targetZoom) > 0.001) {
      this.camera.zoom = THREE.MathUtils.lerp(this.camera.zoom, this.targetZoom, caught);
      this.camera.updateProjectionMatrix();
    }
  }

  /**
   * Set the ground point the camera looks at
   */
  setFocus(x: number, z: number): void {
    this.targetFocus.set(x, 0, z);
  }

  /**
   * Set zoom level
   */
  setZoom(zoom: number): void {
    this.targetZoom = THREE.MathUtils.clamp(zoom, this.minZoom, this.maxZoom);
  }

  /**
   * Cleanup event listeners
   */
  dispose(): void {
    this.domElement.removeEventListener('mousedown', this.onMouseDown);
    this.domElement.removeEventListener('mousemove', this.onMouseMove);
    this.domElement.removeEventListener('mouseup', this.onMouseUp);
    this.domElement.removeEventListener('mouseleave', this.onMouseUp);
    this.domElement.removeEventListener('contextmenu', this.onContextMenu);
    this.domElement.removeEventListener('wheel', this.onWheel);
    this.domElement.removeEventListener('touchstart', this.onTouchStart);
    this.domElement.removeEventListener('touchmove', this.onTouchMove);
    this.domElement.removeEventListener('touchend', this.onTouchEnd);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
  }
}
